/** Pure wording for the Home page (Home spec 2026-09-23 §4). Kept out of
 * `HomeDashboardPage.tsx` so it's testable without rendering the page, and so
 * the component file only exports the component (clears a react-refresh
 * warning). */
import type { Announcements, ScreenReaderInstructions } from '@dnd-kit/core'
import type { HomeGroup, HomeWidth } from '@/components/custom/homeWidgetTypes'

/** The width buttons' words on a card's Customize strip; the page refocuses a pressed one by its word. */
export const WIDTH_WORD: Record<HomeWidth, string> = { 1: 'narrow', 2: 'wide', 3: 'full' }

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** "Monday 14 September 2026" in the given IANA zone, hand-formatted — never
 * `toLocaleString` (SA-460 precedent: untestable across environments). */
export function todayText(now: Date, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  const weekday = DAYS.find((d) => d.startsWith(get('weekday').slice(0, 3))) ?? get('weekday')
  return `${weekday} ${Number(get('day'))} ${MONTHS[Number(get('month')) - 1]} ${get('year')}`
}

/** Drag announcements (SA-475, Home spec §13 FF7): dnd-kit's defaults read
 * internal ids ("Picked up draggable item fleet-strip"); these read the
 * widget's title. Pure — the page passes the title lookup — apart from one
 * per-drag flag: dnd-kit fires `onDragOver` with `over` = the card itself
 * the instant a drag starts (before it has moved anywhere), and without the
 * flag that would silently replace "Picked up X." with "X is back where it
 * started." before a screen reader ever announces the pickup. The flag
 * lives in this closure, reset by every `onDragStart`, so each call to
 * `dragAnnouncements(...)` gets its own fresh state. */
export function dragAnnouncements(titleOf: (id: string) => string): Announcements {
  const t = (x: { id: string | number }) => titleOf(String(x.id))
  let hasLeft = false
  return {
    onDragStart: ({ active }) => {
      hasLeft = false
      return `Picked up ${t(active)}.`
    },
    onDragOver: ({ active, over }) => {
      if (!over) {
        hasLeft = true
        return `${t(active)} is no longer over a widget.`
      }
      if (over.id === active.id) {
        if (!hasLeft) return undefined
        return `${t(active)} is back where it started.`
      }
      hasLeft = true
      return `${t(active)} is over ${t(over)}.`
    },
    onDragEnd: ({ active, over }) => {
      if (!over) return `${t(active)} was dropped.`
      return over.id === active.id ? `${t(active)} was dropped where it started.` : `${t(active)} was dropped over ${t(over)}.`
    },
    onDragCancel: ({ active }) => `Moving ${t(active)} was cancelled.`,
  }
}

export const DRAG_INSTRUCTIONS: ScreenReaderInstructions = {
  draggable: 'To pick up a widget, press space on its handle. Use the arrow keys to move it, space again to drop it, or escape to cancel.',
}

/** The Add list's groups, in the order it draws them (SA-1332, spec §4.7). */
export const GROUP_ORDER: HomeGroup[] = ['aircraft', 'work', 'customers', 'business']

export const GROUP_TITLES: Record<HomeGroup, string> = {
  aircraft: 'What the aircraft are doing',
  work: 'What you need to do',
  customers: 'Where the customers stand',
  business: 'How the business is doing',
}

/** A card too narrow to read that has no single figure to lead with (SA-1406). */
export const HEADLINE_WIDEN = 'Widen the page to see this card.'
